import {ReviewThreadComment} from '@github-ui/diff-lines/review-thread-comment'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import type {Comment} from '@github-ui/diff-lines/conversation-types'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {ChevronRightIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {memo, useCallback} from 'react'

import {ThreadHeader} from './ThreadHeader'
import type {ThreadPreview as ThreadPreviewType} from '../../page-data/payloads/thread-previews'
import {PreviewAuthors} from './PreviewAuthors'
import {useIsSingleFileMode, useMarkerAndDiffNavigationActions} from '../../stores/selectors'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'

function panelThreadCollapsedStateStorageKey(id: string) {
  return `panel-thread-collapsed-state-${id}`
}

type ThreadPreviewProps = {
  onNavigateToDiffComment: () => void
  pullRequestId: string
  tabSize?: number
  thread: ThreadPreviewType
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

/**
 * Renders a preview of a thread in a pull request.
 * It displays the thread's header, the difflines that the
 * thread is associated with, and the first comment of the thread.
 */
export const ThreadPreview = memo(function ThreadPreview({
  onNavigateToDiffComment,
  pullRequestId,
  tabSize,
  thread,
  headingLevel,
}: ThreadPreviewProps) {
  const firstComment = thread.firstComment
  const threadId = thread.threadId
  const isOutdated = thread.isOutdated
  const commentId = firstComment?.id
  const filePath = thread.path
  const isSingleFileMode = useIsSingleFileMode()
  const {navigateToSelectedComment} = useMarkerAndDiffNavigationActions()

  const navigateToComment = useCallback(() => {
    if (isOutdated && thread.originalDiffPathUri) {
      // eslint-disable-next-line no-restricted-syntax
      const url = new URL(thread.originalDiffPathUri)
      url.searchParams.set('new_files_changed', 'true')
      window.location.href = url.toString()
    } else {
      window.location.hash = `#r${firstComment?.databaseId}`
      if (isSingleFileMode && firstComment?.databaseId) {
        navigateToSelectedComment(firstComment?.databaseId)
      }
    }
    onNavigateToDiffComment()
  }, [
    isOutdated,
    thread.originalDiffPathUri,
    onNavigateToDiffComment,
    firstComment?.databaseId,
    isSingleFileMode,
    navigateToSelectedComment,
  ])

  const [isCollapsed, setIsCollapsed] = useLocalStorage(panelThreadCollapsedStateStorageKey(threadId), false)

  const replyCount = thread.threadPreviewComments.length
  const repliesText = replyCount === 0 ? 'No replies' : `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`
  // The StaticUnifiedDiffPreview only looks at the subject, should probably by updated to only require that but for now
  // clean the comments so we don't have to match the typing by requesting a ton more data.
  const threadDiffPreview: ThreadPreviewType & {commentsData: {comments: Comment[]}} = {
    ...thread,
    commentsData: {comments: []},
  }

  if (!firstComment || !threadDiffPreview) return null
  const lineKey = thread.subjectType === 'FILE' ? ('FILE' as FileKey) : (`R${thread.line}` as LineKey)

  return (
    <div className="border rounded-2 d-flex flex-column">
      <ThreadHeader
        firstCommentId={firstComment?.databaseId}
        isCollapsed={isCollapsed}
        thread={thread}
        onNavigateToDiffComment={navigateToComment}
        onToggleCollapsed={() => setIsCollapsed(!isCollapsed)}
        headingLevel={headingLevel}
      />
      {!isCollapsed && (
        <>
          <div className="border-bottom borderColor-muted overflow-x-auto">
            <StaticUnifiedDiffPreview
              diffTableClassName="border-0"
              diffLines={threadDiffPreview.subject?.diffLines}
              className="m-0 border-0"
              tabSize={tabSize || 4}
            />
          </div>
          <div>
            <ReviewThreadComment
              lineMarkersKey={lineKey}
              key={commentId}
              hideActions
              isInlineComment={false}
              comment={firstComment}
              filePath={filePath}
              index={0}
              isAnchorable={false}
              isOutdated={isOutdated}
              isThreadResolved={thread.isResolved || false}
              subjectId={pullRequestId}
              threadId={threadId}
              originalDiffPathUri={thread.originalDiffPathUri}
              headingLevel="h3"
            />
          </div>
          <div className="mb-2 px-2">
            <Button
              aria-label="View comment in diff"
              size="small"
              trailingVisual={ChevronRightIcon}
              variant="invisible"
              onClick={navigateToComment}
            >
              <div className="d-flex flex-row flex-justify-start flex-items-center gap-2">
                <span>{repliesText}</span>
                <PreviewAuthors comments={thread.threadPreviewComments} />
              </div>
            </Button>
          </div>
        </>
      )}
    </div>
  )
})

try{ ThreadPreview.displayName ||= 'ThreadPreview' } catch {}